import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { getPublicFileUrl } from "@/lib/api";
import { usePublicLang } from "@/contexts/PublicLangContext";
import { LANDING } from "@/constants/testIds";

const FALLBACK_HERO = "https://static.prod-images.emergentagent.com/jobs/194e1cf7-0b4d-4fcf-ae44-b4cfcdef1bd6/images/8d74663c693be9516041f13db8d85381c9165712e4d00203a3d43486f221db0f.jpeg";

// Fix BA (2026-10-30): cada cuanto pasa a la foto siguiente y que parte de cada foto se prioriza al recortarla
// (sobre todo en el celular). El encuadre se elige por foto en Configuracion.
const SLIDE_MS = 6000;
const POSITION_CLASS = { center: "object-[50%_20%]", left: "object-[20%_20%]", right: "object-[80%_20%]" };

// Fix B (2026-09-04): hero en dos columnas (texto a la izquierda, foto a la derecha) segun el manual de marca.
// Fix BA (2026-10-30): "colocar como imagen de fondo una fotografia de Strani realizando un tratamiento, de modo que
// ocupe todo el ancho y el alto del encabezado, con la frase destacada encima" y "podriamos ir usando estas imagenes
// para colocarlo como portada" (pedidos de la clinica sobre la pagina web, reemplaza la version de dos columnas).
// Las fotos salen de Configuracion > Pagina > Fotos de la portada (hero_images); con mas de una, pasan solas con un
// fundido. Sin lista, usa hero_image_url (la foto de antes). El velo oscuro de la izquierda y de abajo deja el texto
// legible sobre cualquier foto.
export default function LandingHero({ landing, onBook }) {
  const { t } = usePublicLang();
  const reduceMotion = useReducedMotion();
  const photos = landing.hero_images?.length
    ? landing.hero_images.map((p) => ({ src: getPublicFileUrl(p.url), position: p.position }))
    : [{ src: landing.hero_image_url ? getPublicFileUrl(landing.hero_image_url) : FALLBACK_HERO, position: "center" }];
  const count = photos.length;
  const [active, setActive] = useState(0);
  const current = active % count;

  useEffect(() => {
    if (count < 2 || reduceMotion) return undefined;
    const timer = setTimeout(() => setActive((i) => (i + 1) % count), SLIDE_MS);
    return () => clearTimeout(timer);
  }, [active, count, reduceMotion]);

  const customTitle = landing.hero_title;
  const reveal = (delay) => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 1, delay, ease: [0.16, 1, 0.3, 1] },
  });

  return (
    <section id="top" className="relative min-h-[100svh] bg-[#292522] overflow-hidden flex" data-testid={LANDING.hero}>
      {photos.map((photo, i) => (
        <motion.img
          key={`${photo.src}-${i}`}
          src={photo.src}
          alt=""
          initial={{ scale: 1.06, opacity: 0 }}
          animate={{ scale: i === current ? 1 : 1.06, opacity: i === current ? 1 : 0 }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          className={`absolute inset-0 w-full h-full object-cover ${POSITION_CLASS[photo.position] || POSITION_CLASS.center}`}
          aria-hidden={i !== current}
          data-testid={i === current ? LANDING.heroImage : `${LANDING.heroSlidePrefix}${i}`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-[#292522]/85 via-[#292522]/35 to-[#292522]/25 md:bg-gradient-to-r md:from-[#292522]/85 md:via-[#292522]/45 md:to-transparent" aria-hidden="true" />
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#292522]/55 to-transparent" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-7xl mx-auto flex items-end md:items-center px-6 md:px-10 pt-28 pb-16 md:py-32">
        <motion.div {...reveal(0.2)} className="flex flex-col items-start text-left max-w-xl">
          <h1 className="font-serif text-[2.6rem] sm:text-5xl md:text-[3.8rem] leading-[1.08] tracking-tight text-white drop-shadow-[0_2px_18px_rgba(0,0,0,0.25)]">
            {customTitle ? (
              <span>{customTitle}</span>
            ) : (
              <>
                <span className="block">{t("landing.hero.titleLine1")}</span>
                <span className="sr-only"> </span>
                <span className="block text-[var(--brand-accent)]">{t("landing.hero.titleLine2")}</span>
              </>
            )}
          </h1>

          <span className="block w-16 h-px bg-[var(--brand-primary)] my-6" aria-hidden="true" />

          <p className="text-[11px] sm:text-xs uppercase tracking-[0.26em] text-white/80">
            {landing.hero_subtitle || t("landing.hero.defaultSubtitle")}
          </p>

          <p className="mt-6 text-white/95 text-base md:text-lg leading-relaxed font-serif italic" data-testid={LANDING.heroQuote}>
            {landing.hero_quote || t("landing.hero.defaultQuote")}
          </p>

          {/* Fix BF (2026-11-03): los puntitos van debajo de la frase. Abajo a la derecha quedaban tapados por el boton
              flotante de WhatsApp (computadora y celular). */}
          {count > 1 && (
            <div className="mt-7 -ml-2 flex items-center" data-testid={LANDING.heroDots}>
              {photos.map((photo, i) => (
                <button
                  key={`${photo.src}-${i}`}
                  type="button"
                  onClick={() => setActive(i)}
                  aria-label={`${i + 1} / ${count}`}
                  aria-current={i === current}
                  className="p-2 group"
                  data-testid={`${LANDING.heroDotPrefix}${i}`}
                >
                  <span className={`block h-1.5 rounded-full transition-all duration-500 ${i === current ? "w-8 bg-white" : "w-3 bg-white/50 group-hover:bg-white/80"}`} />
                </button>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        className="hidden md:flex absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-1 text-white/80"
        data-testid={LANDING.scrollIndicator}
      >
        <span className="text-[10px] uppercase tracking-[0.24em]">{t("landing.hero.scrollHint")}</span>
        <ChevronDown className="w-4 h-4" />
      </motion.div>
    </section>
  );
}
